/**
 * localStorageEngine.js — Re Leve IA (Etapa 5)
 * Centraliza toda persistência local no navegador.
 * Regra fundamental: NUNCA salvar CPF, telefone, endereço, nome real.
 * Usar apenas clienteId como identificador.
 */

const PREFIX = 'releve_ia_';

// ── Fallback em memória (caso localStorage não esteja disponível) ──────────────

const memoryStore = {};

function isLocalStorageAvailable() {
  try {
    const test = '__test__';
    localStorage.setItem(test, '1');
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
}

const storageAvailable = isLocalStorageAvailable();

function getItem(key) {
  if (storageAvailable) {
    try {
      return localStorage.getItem(PREFIX + key);
    } catch { /* ignore */ }
  }
  return memoryStore[PREFIX + key] ?? null;
}

function setItem(key, value) {
  if (storageAvailable) {
    try {
      localStorage.setItem(PREFIX + key, value);
      return;
    } catch { /* ignore */ }
  }
  memoryStore[PREFIX + key] = value;
}

function removeItem(key) {
  if (storageAvailable) {
    try {
      localStorage.removeItem(PREFIX + key);
      return;
    } catch { /* ignore */ }
  }
  delete memoryStore[PREFIX + key];
}

function parseJSON(str, fallback) {
  try {
    return str ? JSON.parse(str) : fallback;
  } catch {
    return fallback;
  }
}

function nowISO() {
  return new Date().toISOString();
}

// ── Consentimento ──────────────────────────────────────────────────────────────

export function getStoredConsent(clienteId) {
  return parseJSON(getItem(`consent_${clienteId}`), null);
}

export function setStoredConsent(clienteId, consentData) {
  const record = {
    clienteId,
    dataType: 'consent',
    source: 'local_session',
    timestamp: nowISO(),
    ...consentData,
  };
  setItem(`consent_${clienteId}`, JSON.stringify(record));
  return record;
}

export function clearStoredConsent(clienteId) {
  removeItem(`consent_${clienteId}`);
}

// ── Restrições alimentares ─────────────────────────────────────────────────────

export function getStoredRestrictions(clienteId) {
  return parseJSON(getItem(`restrictions_${clienteId}`), []);
}

export function setStoredRestriction(clienteId, restrictionData) {
  const existing = getStoredRestrictions(clienteId);
  const record = {
    dataType: 'restriction',
    source: 'local_session',
    declaredByUser: true,
    timestamp: nowISO(),
    ...restrictionData,
  };
  const updated = [...existing.filter(r => r.type !== record.type), record];
  setItem(`restrictions_${clienteId}`, JSON.stringify(updated));
  return record;
}

export function clearStoredRestrictions(clienteId) {
  removeItem(`restrictions_${clienteId}`);
}

// ── Logs calóricos ─────────────────────────────────────────────────────────────

export function getStoredCalorieLogs(clienteId) {
  return parseJSON(getItem(`calorie_logs_${clienteId}`), []);
}

export function addCalorieLog(clienteId, log) {
  const existing = getStoredCalorieLogs(clienteId);
  const record = {
    dataType: 'calorie_log',
    source: 'local_session',
    timestamp: nowISO(),
    ...log,
  };
  existing.push(record);
  setItem(`calorie_logs_${clienteId}`, JSON.stringify(existing));
  return record;
}

export function clearCalorieLogs(clienteId) {
  removeItem(`calorie_logs_${clienteId}`);
}

// ── Plano calórico ─────────────────────────────────────────────────────────────

export function getStoredCaloriePlan(clienteId) {
  return parseJSON(getItem(`calorie_plan_${clienteId}`), null);
}

export function setStoredCaloriePlan(clienteId, plan) {
  const record = {
    dataType: 'calorie_plan',
    source: 'local_session',
    timestamp: nowISO(),
    ...plan,
  };
  setItem(`calorie_plan_${clienteId}`, JSON.stringify(record));
  return record;
}

export function clearCaloriePlan(clienteId) {
  removeItem(`calorie_plan_${clienteId}`);
}

// ── Eventos da sessão ──────────────────────────────────────────────────────────

export function getSessionEvents(clienteId) {
  return parseJSON(getItem(`session_events_${clienteId}`), []);
}

export function addSessionEvent(clienteId, event) {
  const existing = getSessionEvents(clienteId);
  const record = {
    dataType: 'event',
    source: 'local_session',
    timestamp: nowISO(),
    ...event,
  };
  existing.push(record);
  setItem(`session_events_${clienteId}`, JSON.stringify(existing));
  return record;
}

export function clearSessionEvents(clienteId) {
  removeItem(`session_events_${clienteId}`);
}

// ── Estado completo do cliente ─────────────────────────────────────────────────

export function getCustomerLocalState(clienteId) {
  if (!clienteId) return null;
  return {
    clienteId,
    consent: getStoredConsent(clienteId),
    restrictions: getStoredRestrictions(clienteId),
    calorieLogs: getStoredCalorieLogs(clienteId),
    caloriePlan: getStoredCaloriePlan(clienteId),
    sessionEvents: getSessionEvents(clienteId),
  };
}

// ── Limpar TODOS os dados locais de um cliente ────────────────────────────────

export function clearAllClientData(clienteId) {
  clearStoredConsent(clienteId);
  clearStoredRestrictions(clienteId);
  clearCalorieLogs(clienteId);
  clearCaloriePlan(clienteId);
  clearSessionEvents(clienteId);
}
